import * as React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import * as _ from 'underscore';

import { connect } from 'react-redux';
import { selectCatalogingOnSiteEnabled } from '../../../catalog/selectors/CatalogSelectors';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { Omit } from '../../../ts/helpers/TypescriptHelpers';
import { selectPreviousUrl } from '../../selectors/NavigationStateSelectors';
import { CompositeSessionInfoSelectors, SessionSiteSelectors } from '../../selectors/SessionInfoSelectors';
import { AppState } from '../../state/AppState';
import { DatasourceSubspaceName } from '../../state/SpaceInfo';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { RouterError } from '../RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { DatasourceSpaceProvider, DatasourceSpaceProviderProps } from './DatasourceSpaceProvider';
import { FeatureFlags } from '~src/ts/FeatureFlags';

export interface SpaceRouteParams {
  readonly id: string;
}

export interface DatasourceSpaceRouteComponentProps extends RouteComponentProps<SpaceRouteParams> {}

export type DatasourceProviderFnReturnType = (props: DatasourceSpaceRouteComponentProps) => JSX.Element;
export type DatasourceProviderFn = (subspaceName: DatasourceSubspaceName, providerProps: DatasourceSpaceComponentWithRouterProps) => DatasourceProviderFnReturnType;
export interface DatasourceSpaceRouterOwnProps extends ReactDirectiveProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly provider: DatasourceProviderFn;
}

export interface DatasourceSpaceRouterStateProps {
  readonly catalogingOnSiteEnabled: boolean;
  readonly isNLPServiceEnabled: boolean;
  readonly previousUrl: string;
  readonly isWebExtractionEnabled: boolean;
}

export type DatasourceSpaceRouterProps = DatasourceSpaceRouterOwnProps & DatasourceSpaceRouterStateProps;

export class DatasourceSpaceRouterComponent extends React.Component<DatasourceSpaceRouterProps> {

  public render() {
    return (
      <Switch>
        {this.getAskDataRoute()}
        <Route exact path={this.props.urls.datasourceConnectionsPath(':id')} render={this.props.provider('connections', this.props)} />
        <Route exact path={this.props.urls.datasourceExtractRefreshesPath(':id')} render={this.props.provider('extractRefreshes', this.props)} />
        <Route exact path={this.props.urls.datasourceLocalExtractRefreshesPath(':id')} render={this.props.provider('remoteRefreshSchedules', this.props)} />
        <Route exact path={this.props.urls.datasourceConnectedWorkbooksPath(':id')} render={this.props.provider('connectedWorkbooks', this.props)} />
        {this.getLineageRoute()}
        {this.getAskDataUsageAnalyticsRoute()}

        <Route exact path={this.props.urls.datasourcePublishresultPath(':id')} render={({ match }) => (
          // this has to be datasourceAskDataPath and not datasourcePath so that displayPublishResult is
          // populated correctly because it checks the publishresult url from the redirect. If we insert
          // another redirect between /publishresult and rendering the provider, the previous url
          // is wrong.
          <Redirect to={this.props.urls.datasourceAskDataPath(match.params.id)} />
        )} />
        <Route exact path={this.props.urls.datasourceDetailsPath(':id')} render={({ match }) => (
          <Redirect to={this.props.urls.datasourceAskDataPath(match.params.id)} />
        )} />

        <Route exact path={this.props.urls.datasourceDataConnectionPath(':id')} render={({ match }) => (
          // The Integrated Datasources feature removed the "data connection" page for datasources and
          // replaced it with the "data connections" page. If any old links point to the old page, this
          // redirect will get them to the right place.
          <Redirect to={this.props.urls.datasourceConnectionsPath(match.params.id)} />
        )} />

        <Route exact path={this.props.urls.datasourcePath(':id')} render={({ match }) => (
          <Redirect to={this.props.urls.datasourceAskDataPath(match.params.id)} />
        )} />

        <Route render={this.error} />
      </Switch>
    );
  }

  private error = (): JSX.Element => {
    return <RouterError errorType={'default'} urls={this.props.urls}/>;
  }

  private getAskDataRoute = (): JSX.Element => {
    if (this.props.isNLPServiceEnabled) {
      return <Route exact path={this.props.urls.datasourceAskDataPath(':id')} render={this.props.provider('nlp', this.props)} />;

    } else {
      return (
        <Route exact path={this.props.urls.datasourceAskDataPath(':id')} render={({ match }) => (
          <Redirect to={this.props.urls.datasourceConnectionsPath(match.params.id)} />
        )} />);
    }
  }

  private getAskDataUsageAnalyticsRoute = (): JSX.Element => {
    if (this.props.isNLPServiceEnabled && !FeatureFlags.nlplensContentTypeEnabled) {
      return <Route exact path={this.props.urls.datasourceAskDataUsageAnalyticsPath(':id')} render={this.props.provider('askDataUsageAnalytics', this.props)} />;

    } else {
      return (
        <Route exact path={this.props.urls.datasourceAskDataUsageAnalyticsPath(':id')} render={({ match }) => (
          <Redirect to={this.props.urls.datasourceConnectionsPath(match.params.id)} />
        )} />);
    }
  }

  private getLineageRoute = (): JSX.Element => {
    if (this.props.catalogingOnSiteEnabled) {
      return <Route path={this.props.urls.datasourceLineagePath(':id')} render={this.props.provider('lineage', this.props)} />;
    } else {
      // we want to match on any data source path starting with /lineage so don't use 'exact' on the route
      return (
        <Route path={this.props.urls.datasourceLineagePath(':id')} render={({ match }) => (
          <Redirect to={this.props.urls.datasourceConnectionsPath(match.params.id)} />
        )} />);
    }
  }
}

export type DatasourceSpaceComponentWithRouterProps = Omit<DatasourceSpaceRouterProps, 'provider'>;
const provider: DatasourceProviderFn = (subspaceName: DatasourceSubspaceName, providerProps: DatasourceSpaceComponentWithRouterProps): DatasourceProviderFnReturnType => {
  return (props: DatasourceSpaceRouteComponentProps): JSX.Element => {
    const datasourceId = props.match.params.id;
    const displayPublishResult = providerProps.previousUrl.includes(providerProps.urls.datasourcePublishresultPath(datasourceId));

    const datasourceSpaceProviderProps: DatasourceSpaceProviderProps = {
      siteName: providerProps.siteName,
      subspaceName,
      routeLocationSearch: props.location.search,
      routeLocationPath: props.location.pathname,
      datasourceId,
      isNLPServiceEnabled: providerProps.isNLPServiceEnabled,
      urls: providerProps.urls,
      angularContext: providerProps.angularContext,
      displayPublishResult,
      publishResultUrl: displayPublishResult ? providerProps.previousUrl : '',
      isWebExtractionEnabled: providerProps.isWebExtractionEnabled,
    };

    return <DatasourceSpaceProvider {...datasourceSpaceProviderProps} />;
  };
};

type DatasourceSpaceComponentWithRouterOwnProps = Omit<DatasourceSpaceRouterOwnProps, 'urls' | 'provider'>;
const DatasourceSpaceComponentWithRouter = (props: DatasourceSpaceComponentWithRouterProps) => {
  return (
    <DatasourceSpaceRouterComponent {...props} provider={provider}/>
  );
};

const mapStateToProps: MapStateToProps<DatasourceSpaceRouterStateProps> = (state: AppState) => ({
  previousUrl: selectPreviousUrl(state),
  catalogingOnSiteEnabled: selectCatalogingOnSiteEnabled(state),
  isNLPServiceEnabled: SessionSiteSelectors.isNLPServiceEnabled(state),
  isWebExtractionEnabled: CompositeSessionInfoSelectors.isWebExtractionEnabled(state)
});

// Component must *not* be pure as to allow route updates to flow through
export const DatasourceSpaceRouter: React.ComponentClass<DatasourceSpaceComponentWithRouterOwnProps>
  = connect(mapStateToProps, null, null, {pure: false})(DatasourceSpaceComponentWithRouter);
